import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import NoAccessPage from "components/error/NoAccessPage";
import WarningContextProvider from "components/WarningContext/Provider";
import Box from "ds/components/Box";
import FullScreenModal from "ds/components/FullScreenModal";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import useAnalytics from "hooks/useAnalytics";
import useCanCreateContext from "shared/Context/access/useCanCreateContext";

import NewContextAddHooks from "./AddHooks";
import NewAttachContext from "./AttachContext";
import { AttachedProject } from "./AttachedProject/types";
import {
  CONTEXT_ANALYTICS_VERSION,
  ContextCreationWizardStep,
  contextCreationWizardNavSteps,
  defaultHooksValues,
} from "./constants";
import { ContextFormContext } from "./context";
import NewContextDetails from "./Details";
import NewContextSetupEnvironment from "./SetupEnvironment";
import NewContextStepper from "./Stepper";
import NewContextSummary from "./Summary";
import { ContextFormFields } from "./types";

const defaultValues = {
  description: "",
  labels: [],
  contextName: "",
  space: "",
  attachedProjectIds: [],
  attachedVariables: [],
  attachedMountedFiles: [],
  hooks: defaultHooksValues,
};

type NewContextProps = {
  initialStep?: ContextCreationWizardStep;
};

const NewContext = ({ initialStep = ContextCreationWizardStep.Details }: NewContextProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [attachedProjects, setAttachedProjects] = useState<AttachedProject[]>([]);
  const [autoAttachedProjects, setAutoAttachedProjects] = useState<AttachedProject[]>([]);
  const navigate = useNavigate();

  const handleStepChange = useCallback((step: ContextCreationWizardStep) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrentStep((prevStep) =>
      Math.min(Object.keys(ContextCreationWizardStep).length, prevStep + 1)
    );
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prevStep) => Math.max(0, prevStep - 1));
  }, []);

  const currentStepData = contextCreationWizardNavSteps.find((item) => item.step === currentStep);

  const trackSegmentAnalyticsEvent = useAnalytics({
    defaultCallbackTrackProperties: {
      location: currentStepData?.shortName,
      version: CONTEXT_ANALYTICS_VERSION,
    },
  });

  const contextFormContextValue = useMemo(() => {
    const setProjectsData = (data: AttachedProject[], isAuto?: boolean) => {
      if (isAuto) {
        setAutoAttachedProjects(data);
      } else {
        setAttachedProjects(data);
      }
    };
    const stepCount = Object.keys(ContextCreationWizardStep).length / 2;
    const lastStep = stepCount - 1;
    return {
      currentStep,
      currentStepData,
      setCurrentStep: handleStepChange,
      goToNextStep,
      goToPreviousStep,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === lastStep,
      attachedProjects,
      autoAttachedProjects,
      setAttachedProjects: setProjectsData,
    };
  }, [
    currentStep,
    currentStepData,
    goToNextStep,
    goToPreviousStep,
    handleStepChange,
    attachedProjects,
    autoAttachedProjects,
  ]);

  const form = useForm<ContextFormFields>({
    defaultValues,
    mode: "onChange",
  });

  const modalCloseClickHandler = useCallback(async () => {
    if (form.formState.isDirty) {
      await showSimpleLeaveConfirmation({
        title: "Do you want to leave context creation process?",
        message: "Your changes will not be saved.",
      });
    }

    trackSegmentAnalyticsEvent("Wizard - Exited");
    navigate("/contexts");
  }, [navigate, trackSegmentAnalyticsEvent, form.formState.isDirty]);

  const canCreateContext = useCanCreateContext();

  if (!canCreateContext.result) {
    return <NoAccessPage />;
  }

  return (
    <ContextFormContext.Provider value={contextFormContextValue}>
      <FullScreenModal title="Create context" onClose={modalCloseClickHandler}>
        <WarningContextProvider>
          <Box grow="1" fullWidth scrollable>
            <NewContextStepper />
            <Box fullWidth direction="column" relative>
              <FormProvider {...form}>
                {currentStep === ContextCreationWizardStep.Details && <NewContextDetails />}
                {currentStep === ContextCreationWizardStep.Attachment && <NewAttachContext />}
                {currentStep === ContextCreationWizardStep.Environment && (
                  <NewContextSetupEnvironment />
                )}
                {currentStep === ContextCreationWizardStep.Hooks && <NewContextAddHooks />}
                {currentStep === ContextCreationWizardStep.Summary && <NewContextSummary />}
              </FormProvider>
            </Box>
          </Box>
        </WarningContextProvider>
      </FullScreenModal>
    </ContextFormContext.Provider>
  );
};

export default NewContext;
